import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { map } from 'rxjs/operators';
import { AppConfigService } from '@core/services/app-config/app-config.service';
import { ClientContextPersistenceService } from '@core/services/client-context-persistence/client-context-persistence.service';
import { EnvironmentService } from '@core/services/environment/environment.service';

/**
 * Guard que evalúa la configuración global de redirección al visitar la ruta raíz.
 *
 * Prioridad:
 *   1. redirect_slug → navegación interna a /:slug (Angular Router)
 *   2. redirect_url → redirección externa (window.location.href)
 *   3. Ninguno activo → continúa al HomeComponent normalmente
 *
 * Si el endpoint del backend falla o no existe, devuelve null y se permite
 * el acceso normal sin interrupciones.
 */
export const appRedirectGuard: CanActivateFn = () => {
  const appConfigService = inject(AppConfigService);
  const clientContextPersistence = inject(ClientContextPersistenceService);
  const router = inject(Router);
  const environment = inject(EnvironmentService);

  // Durante SSR no hay window.location ni necesidad de redirigir
  if (environment.isServer()) {
    return true;
  }

  const cached = clientContextPersistence.get();
  if (cached) {
    return router.createUrlTree([cached.slug]);
  }

  return appConfigService.getRedirectConfig().pipe(
    map((config) => {
      if (!config) return true;

      if (config.redirect_slug) {
        return router.createUrlTree([config.redirect_slug]);
      }

      if (config.redirect_url) {
        window.location.href = config.redirect_url;
        return false;
      }

      return true;
    }),
  );
};
